var CacheStore                = require("app/core/CacheStore"),
    Component                 = require("enyo/Component"),
    circularSafeJSONStringify = require("app/utils/circularSafeJSONStringify"),
    kind                      = require("enyo/kind"),
    logger                    = require("enyo/logger");

/**
 * Basic infrastructure for loading data from OTT Api V2 by means of GET
 * requests.
 *
 * @abstract
 */
module.exports = kind(/** @lends app.Domain.Ott.Api.V2.Repository.prototype */ {

    /**
     * @private
     */
    name: "app.Domain.Ott.Api.V2.Repository",

    /**
     * @private
     */
    kind: Component,

    /**
     * @type {Object}
     * @private
     */
    tasks: null,

    /**
     * @type  {app.Auth.AuthService}
     * @private
     */
    authService: null,

    /**
     * The regular expression against which the router will match incoming
     * pathname.
     *
     * @type {app.core.CacheStore}
     * @public
     */
    cache: new CacheStore(),

    /**
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.tasks = {};
        };
    }),

    /**
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.onAuthChange = this.bindSafely(this.onAuthChange);
        };
    }),

    // API
    // --------------------------------

    /**
     * Clears all cached values
     *
     * @public
     */
    clearCache: function() {
        this.cache.reset();
    },

    /**
     * Always returns an empty request.
     *
     * @returns  {Object}  A Promise that resolves with the API response
     * @public
     */
    noop: function() {
        return Promise.resolve({
            status: null,
            data:   null
        });
    },

    // Implementation
    // --------------------------------

    /**
     * @param  {app.Auth.AuthService}  $oldAuthService  The previous
     *                                                  authService.
     * @private
     */
    authServiceChanged: function($oldAuthService) {
        var $newAuthService = this.get("authService");

        if ($oldAuthService) {
            $oldAuthService.off("Auth::loggedIn", this.onAuthChange);
            $oldAuthService.off("Auth::loggedOut", this.onAuthChange);
        }

        if ($newAuthService) {
            $newAuthService.on("Auth::loggedIn", this.onAuthChange);
            $newAuthService.on("Auth::loggedOut", this.onAuthChange);
        }
    },

    /**
     * Clear cache and renew pending taks when auth status changes.
     *
     * @private
     */
    onAuthChange: function() {
        var key,
            task;

        this.clearCache();

        /* eslint-disable guard-for-in */
        for (key in this.tasks) {
            task           = this.tasks[key];
            !task.finished && task.renew();
        }
        /* eslint-enable guard-for-in */
    },

    /**
     * Create a task for a HTTP GET request, which exposes a promise and
     * executes an XMLHttpRequest. The promise resolves once the XMLHttpRequest
     * is done.
     *
     * The returned task can also be renewed, causing the pending XMLHttpRequest
     * to be aborted and a new one to be created with the same parameters. The
     * promise resolves as soon as the pending XMLHttpRequest is done.
     *
     * @param    {string}  url  The url to get
     * @returns  {Object}  The get task
     * @protected
     */
    _get: function(url) {
        var task = this._buildXhrTask();

        // Executes a GET request
        task.execute = function() {
            var xhr;

            task.xhr = xhr = new XMLHttpRequest();
            xhr.open("GET", url);
            xhr.setRequestHeader("Accept", "application/json; charset=UTF-8");

            xhr.onreadystatechange = function() {
                if (xhr.readyState === 4) {
                    if (xhr.status === 0 && task.isRenewing) {
                        // We are renewing the task, we can skip handling the
                        // canceled response and await the result of the renewed
                        // task.
                    }
                    else {
                        task.handleResult();
                    }
                }
            };
            xhr.onreadystatechange.bind(this);
            xhr.send();
        };

        // Handles the result of an XMLHttpRequest and resolves the promise
        task.handleResult = function() {
            var response,
                xhr     = task.xhr,
                resolve = task.resolve;

            try {
                if (xhr.status < 500) {
                    response = xhr.responseText && xhr.responseText !== "" ? JSON.parse(xhr.responseText) : null;
                }
                else {
                    response = null;
                }
            }
            catch (error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error, {
                        extra: {
                            xhr: circularSafeJSONStringify(xhr)
                        }
                    });
                }

                // JSON response expected
                resolve({
                    status:     "error",
                    type:       "invalid_response",
                    httpStatus: xhr.status
                });
            }

            // Request was canceled
            if (xhr.status === 0) {
                resolve({
                    status: "canceled"
                });
            }

            // Data received
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(response);
            }

            // Not found
            else if (xhr.status === 404) {
                resolve({
                    status:     "error",
                    type:       response && response.type ? response.type : "not_found_error",
                    httpStatus: xhr.status
                });
            }

            // Error received
            else if (xhr.status < 500) {
                resolve({
                    status:     "error",
                    type:       response && response.type ? response.type : "unknown_error",
                    httpStatus: xhr.status
                });
            }

            // Server error
            else {
                resolve({
                    status:     "error",
                    type:       response && response.type ? response.type : "server_error",
                    httpStatus: xhr.status
                });
            }
        };

        // Run the task
        task.execute();

        return task;
    },

    /**
     * Create a task object for an XMLHttpRequest request. This method creates
     * only the task, not the request itself.
     *
     * @returns  {Object}  The task
     * @protected
     */
    _buildXhrTask: function() {
        var task = {
            finished:   false,
            isRenewing: false
        };

        task.promise = new Promise(function(resolve, reject) {
            task.resolve = function(result) {
                task.finished = true;
                resolve(result);
            };

            // Allows for manually rejecting the task. Rejecting it will abort
            // the XMLHttpRequest if one is pending.
            task.reject  = function(reason) {
                if (task.xhr.readyState !== 4) {
                    task.xhr.abort();
                }
                task.finished = true;
                reject(reason);
            };
        });

        // Aborts the pending XMLHttpRequest
        task.abort = function() {
            if (task.xhr.readyState !== 0 && task.xhr.readyState !== 4) {
                task.xhr.abort();
            }
        };

        // Abort the current XMLHttpRequest and start a new one
        task.renew = function() {
            task.isRenewing = true;
            task.abort();
            task.execute();
            task.isRenewing = false;
        };

        return task;
    }

});
